// The progress PDF, printed by the browser.
//
// Same route shape as /gantt-print and /schedule-print: a plain page the
// Print button opens in a tab, styled for paper and told to print itself
// once it has data. No PDF library - the browser already has one, and a
// server-side renderer for a page this simple is a service to maintain
// for no gain.
import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { getBoard, getAnalytics } from "api/tracking/fieldTrackingApi";
import { renderPdfThumbnail } from "helpers/pdfPreview";
import SheetStage from "./SheetStage";
import { STATUS_ORDER, STATUS_COLOR, STATUS_LABEL, STATUS_DASH, systemLabel } from "./trackingTheme";

// THE DRAWING IS THE REPORT.
//
// A progress report that is only a table asks the reader to hold a
// hundred marks in their head and picture where they are. The sheet with
// the runs coloured by status answers "what is left" at a glance, which
// is the whole reason the page has a drawing on it - so the print takes
// the same overlay, in the same percent space, and bakes it in.
//
// Rendered as inline SVG over the rasterised sheet rather than a
// screenshot of the pane: it stays sharp at print resolution and the
// browser's own print pipeline handles it without a canvas round trip.
function rollup(item) {
  const segs = item.segments || [];
  if (!segs.length) return "projected";
  let worst = "installed";
  for (const s of segs) {
    const i = STATUS_ORDER.indexOf(s.status);
    if (i >= 0 && i < STATUS_ORDER.indexOf(worst)) worst = s.status;
  }
  return worst;
}

function SheetPlate({ sheet, img, items }) {
  const on = items.filter((i) => String(i.sheetDocControlId) === String(sheet._id) && (i.points || []).length);
  if (!img || !on.length) return null;
  return (
    <div style={{ breakInside: "avoid", marginBottom: 18 }}>
      <h3 style={{ fontSize: 13, margin: "0 0 6px" }}>
        {sheet.sheetNumber} {sheet.sheetName}
        <span style={{ fontSize: 11, color: "#8D8D8D", fontWeight: 400 }}> · {on.length} tracked</span>
      </h3>
      {/* The same stage the board uses, so the printed overlay registers
          to the drawing exactly as it does on screen. */}
      <SheetStage img={img} imgOpacity={1} outerStyle={{ border: "1px solid #EFEFEF" }}>
          {on.map((item) => {
            const segs = (item.segments || []).filter((sg) => (sg.points || []).length >= 2);
            const parts = segs.length
              ? segs
              : [{ key: "_all", points: item.points, status: rollup(item) }];
            return parts.map((sg) => {
              const pts = (sg.points || []).map((p) => `${p.x},${p.y}`).join(" ");
              const colour = STATUS_COLOR[sg.status] || STATUS_COLOR.projected;
              if ((sg.points || []).length === 1 || item.kind === "gear") {
                const p = (sg.points || item.points)[0];
                return <circle key={`${item._id}-${sg.key}`} cx={p.x} cy={p.y} r={1}
                               fill={colour} stroke="#fff" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />;
              }
              return (
                <g key={`${item._id}-${sg.key}`}>
                  <polyline points={pts} fill="none" stroke="#fff" strokeWidth={5}
                            strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                  {/* Dashed by status as well as coloured: a progress
                      report is routinely photocopied and carried round a
                      site, and the pattern survives that when the colour
                      does not. */}
                  <polyline points={pts} fill="none" stroke={colour} strokeWidth={3}
                            strokeDasharray={STATUS_DASH[sg.status] || undefined}
                            strokeLinecap={STATUS_DASH[sg.status] ? "butt" : "round"}
                            vectorEffect="non-scaling-stroke" />
                </g>
              );
            });
          })}
      </SheetStage>
    </div>
  );
}

const pct = (n) => `${Math.round((Number(n) || 0) * 10) / 10}%`;

export default function TrackingPrintPage() {
  const { projectId } = useParams();
  const q = new URLSearchParams(useLocation().search);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const [plates, setPlates] = useState({});

  useEffect(() => {
    let alive = true;
    // The first thousand items, with the same filter the page had. A
    // printed table of 35,000 rows is not a report; the totals above it
    // are over every item regardless.
    Promise.all([
      getBoard(projectId, {
        limit: 1000, from: q.get("from") || undefined, to: q.get("to") || undefined,
        system: q.get("system") || undefined, status: q.get("status") || undefined,
      }),
      getAnalytics(projectId, { from: q.get("from") || undefined, to: q.get("to") || undefined, system: q.get("system") || undefined }),
    ])
      .then(async ([board, analytics]) => {
        if (!alive) return;
        setData({ board, analytics });
        // Only the sheets that actually carry tracked items: rasterising
        // 163 drawings to print five of them would take minutes and
        // print nothing extra.
        const used = new Set((board.drawn || [])
          .map((i) => String(i.sheetDocControlId)));
        const wanted = (board.sheets || []).filter((sh) => used.has(String(sh._id)));
        const out = {};
        for (const sh of wanted) {
          // Serial, not parallel: pdf.js on a 2-core box is happier one
          // document at a time, and the print is not a race.
          // eslint-disable-next-line no-await-in-loop
          out[String(sh._id)] = await renderPdfThumbnail(sh.link, { scale: 2, maxWidth: 2000 }).catch(() => null);
        }
        if (!alive) return;
        setPlates(out);
        // Let the images decode and the layout settle before handing it
        // to the print dialog, or the first page comes out half-drawn.
        setTimeout(() => { try { window.print(); } catch (e) { /* user can print manually */ } }, 900);
      })
      .catch((e) => { if (alive) setErr(e.message || "Could not load"); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (err) return <div style={{ padding: 40, fontFamily: "Arial" }}>{err}</div>;
  if (!data) return <div style={{ padding: 40, fontFamily: "Arial" }}>Preparing the progress report — rasterising the drawings…</div>;

  const { board, analytics } = data;
  const statusFilter = q.get("status");
  const items = board.items || [];
  const drawn = (board.drawn || []).filter((i) => !statusFilter || i.rollupStatus === statusFilter);
  const t = analytics.totals || {};

  return (
    <div style={{ fontFamily: "Arial, sans-serif", color: "#23343E", padding: 24, background: "#fff" }}>
      <style>{`@page { size: A4 landscape; margin: 12mm; }
        @media print { .no-print { display: none; } tr { break-inside: avoid; } thead { display: table-header-group; } }
        table { border-collapse: collapse; width: 100%; font-size: 11px; }
        th, td { border-bottom: 1px solid #EFEFEF; padding: 5px 6px; text-align: left; }
        th { font-size: 10px; letter-spacing: .05em; color: #8D8D8D; text-transform: uppercase; }
        td.num { text-align: right; font-variant-numeric: tabular-nums; }`}</style>

      <h2 style={{ margin: "0 0 2px" }}>Installation progress</h2>
      <div style={{ fontSize: 11, color: "#8D8D8D", marginBottom: 14 }}>
        {q.get("from") || q.get("to")
          ? `Window ${q.get("from") || "start"} → ${q.get("to") || "today"}`
          : "Whole project to date"}
        {q.get("system") ? ` · ${systemLabel(q.get("system"))}` : ""}
        {" · "}printed {new Date().toLocaleString()}
      </div>

      <div style={{ display: "flex", gap: 24, marginBottom: 16 }}>
        <div><div style={{ fontSize: 10, color: "#8D8D8D" }}>TRACKED</div><div style={{ fontSize: 20, fontWeight: 700 }}>{t.items || 0}</div></div>
        <div><div style={{ fontSize: 10, color: "#8D8D8D" }}>INSTALLED</div><div style={{ fontSize: 20, fontWeight: 700 }}>{pct(t.percentInstalled)}</div></div>
        <div><div style={{ fontSize: 10, color: "#8D8D8D" }}>FEET IN</div><div style={{ fontSize: 20, fontWeight: 700 }}>{t.installed || 0} / {t.total || 0}</div></div>
      </div>

      {Object.keys(plates).length > 0 && (
        <>
          <h3 style={{ fontSize: 13, margin: "0 0 4px" }}>Drawings, coloured by status</h3>
          <div style={{ display: "flex", gap: 14, marginBottom: 8, flexWrap: "wrap" }}>
            {STATUS_ORDER.map((st) => (
              <span key={st} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11 }}>
                <svg width="26" height="7" style={{ display: "block" }}>
                  <line x1="0" y1="3.5" x2="26" y2="3.5" stroke={STATUS_COLOR[st]} strokeWidth="3"
                        strokeDasharray={STATUS_DASH[st] || undefined} />
                </svg>
                {STATUS_LABEL[st]}
              </span>
            ))}
          </div>
          {(board.sheets || []).map((sh) => (
            <SheetPlate key={String(sh._id)} sheet={sh} img={plates[String(sh._id)]} items={drawn} />
          ))}
        </>
      )}

      {/* CUSTOM CATEGORIES (owner, 2026-10-05: "make sure the print pdf and
          the export are showing the custom categories too").
          BEFORE "By system" and in its own table, because the system table
          is feet of board item and these are PARTS inside the elements
          already counted there. The caveat is printed, not implied: a
          printout is read away from the screen that would have explained
          it. */}
      {(analytics.derived || []).length ? (
        <>
          <h3 style={{ fontSize: 13, margin: "0 0 6px" }}>Custom categories</h3>
          <div style={{ fontSize: 10, color: "#6B7280", margin: "0 0 6px" }}>
            Parts counted inside the tracked elements. A part takes the status of the element it
            belongs to. The parents are already counted below, so these are not part of those totals.
          </div>
          <table style={{ marginBottom: 18 }}>
            <thead>
              <tr>
                <th>Custom category</th><th>From</th><th>Parts</th><th>Parents</th><th>Levels</th>
                {STATUS_ORDER.map((st) => <th key={st}>{STATUS_LABEL[st]}</th>)}
                <th>Installed</th>
              </tr>
            </thead>
            <tbody>
              {(analytics.derived || []).map((d) => {
                const at = (k) => ((d.status && d.status[k] && d.status[k].n) || 0);
                const n = d.n || 0;
                if (d.tooManyParents) {
                  return (
                    <tr key={d.key}>
                      <td>{d.label}</td>
                      <td>{d.sourceCategory || ""}</td>
                      <td className="num" colSpan={3 + STATUS_ORDER.length}>
                        {`too many parent elements to measure (${Number(d.tooManyParents).toLocaleString()})`}
                      </td>
                      <td className="num">—</td>
                    </tr>
                  );
                }
                return (
                  <tr key={d.key}>
                    <td>{d.label}</td>
                    <td>{d.sourceCategory || ""}</td>
                    <td className="num">{n.toLocaleString()}</td>
                    <td className="num">{(d.parentRows || 0).toLocaleString()}</td>
                    <td>{(d.levels || []).join(", ")}</td>
                    {STATUS_ORDER.map((st) => <td key={st} className="num">{at(st).toLocaleString()}</td>)}
                    <td className="num">{n ? pct((at("installed") / n) * 100) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      ) : null}

      <h3 style={{ fontSize: 13, margin: "0 0 6px" }}>By system</h3>
      <table style={{ marginBottom: 18 }}>
        <thead><tr><th>System</th><th>Items</th>{STATUS_ORDER.map((s) => <th key={s}>{STATUS_LABEL[s]}</th>)}<th>Total ft</th><th>Installed</th></tr></thead>
        <tbody>
          {(analytics.bySystem || []).map((s) => (
            <tr key={s.system}>
              <td>{systemLabel(s.system)}</td>
              <td className="num">{s.items}</td>
              {STATUS_ORDER.map((st) => <td key={st} className="num">{Math.round(((s.byStatus || {})[st] || 0) * 10) / 10}</td>)}
              <td className="num">{Math.round(s.total * 10) / 10}</td>
              <td className="num">{s.total ? pct((s.installed / s.total) * 100) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 style={{ fontSize: 13, margin: "0 0 6px" }}>
        Items ({items.length.toLocaleString()}{board.total > items.length ? ` of ${board.total.toLocaleString()} — the first ${items.length.toLocaleString()} printed` : ""})
      </h3>
      <table>
        <thead><tr><th>Mark</th><th>System</th><th>Size</th><th>Level</th><th>Grid</th><th>Status</th><th>Installed</th><th>Last date</th></tr></thead>
        <tbody>
          {items.map((i) => {
            const dates = (i.segments || []).map((s) => s.statusDate).filter(Boolean).sort();
            return (
              <tr key={i._id}>
                <td>{i.mark || i.name || "—"}</td>
                <td>{systemLabel(i.system)}</td>
                <td>{i.size || "—"}</td>
                <td>{i.level || "—"}</td>
                <td>{i.grid || "—"}</td>
                <td>
                  <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: STATUS_COLOR[i.rollupStatus], marginRight: 5 }} />
                  {STATUS_LABEL[i.rollupStatus] || i.rollupStatus}
                </td>
                <td className="num">{pct(i.progress?.percentInstalled)}</td>
                <td>{dates.length ? new Date(dates[dates.length - 1]).toLocaleDateString() : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
